import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useMe } from '@/lib/auth'
import { usePortfolio } from '@/lib/portfolio'
import { useTRPC } from '@/lib/trpc'
import { primaryWallet, type StrategyTemplate } from '@/lib/api-types'
import { LAMPORTS_PER_SOL } from '@/lib/format'
import { WalletSetup } from '@/components/wallet-setup'
import { StrategiesSection } from '@/components/strategies-section'
import { StrategyDetailModal } from '@/components/strategy-detail-modal'
import { StrategyWizard } from '@/components/strategy-wizard'

export function StrategiesPage() {
  const trpc = useTRPC()
  const { user } = useMe()
  const wallet = primaryWallet(user)
  const ready = Boolean(wallet && user?.walletSetupComplete)
  const [params, setParams] = useSearchParams()
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  /** the strategy a new one copies (John, Nov 3 2026): the wizard opens filled in with it */
  const [template, setTemplate] = useState<StrategyTemplate | null>(null)
  const duplicate = (strategy: StrategyTemplate) => {
    setSelectedId(null)
    setTemplate(strategy)
    setCreating(true)
  }
  useEffect(() => {
    const id = params.get('id') ?? params.get('strategy')
    if (id) setSelectedId(id)
    if (params.get('create')) setCreating(true)
    if (id || params.get('create')) setParams({}, { replace: true })
  }, [params, setParams])
  const strategies = useQuery({ ...trpc.strategy.list.queryOptions(), enabled: ready, refetchInterval: 15_000 })
  // the home page's portfolio query (shared, polled at its pace): the strategy rows read a held token's market cap off it
  const portfolio = usePortfolio(wallet, ready)

  if (!ready || !wallet) return <WalletSetup />
  const list = strategies.data ?? []
  const solLamports = portfolio.data?.native?.solana?.balance
  const solBalance = solLamports !== undefined ? Number(solLamports) / LAMPORTS_PER_SOL : undefined
  const holdings = [...(portfolio.data?.tokens ?? []), ...(portfolio.data?.vault ?? [])]

  return (
    <div className="grid gap-6 *:min-w-0">
      {/* the home page's strategies block, with the finished rows open too */}
      <StrategiesSection strategies={list} holdings={holdings} pending={strategies.isPending} failed={strategies.isError} completedOpen onCreate={() => setCreating(true)} onSelect={setSelectedId} onDuplicate={duplicate} />

      <StrategyWizard
        open={creating}
        onClose={() => {
          setCreating(false)
          setTemplate(null)
        }}
        wallet={wallet}
        holdings={holdings}
        solBalance={solBalance}
        template={template}
      />
      <StrategyDetailModal id={selectedId} onClose={() => setSelectedId(null)} onDuplicate={duplicate} />
    </div>
  )
}
